import { GuestFormValues } from '@model/forms';
import { GuestMapper } from '@model/iceberg/booking/basket/util/guest-mapper';
import { GuestsApi, GuestsResponse } from '@model/iceberg/service/booking/guests-api';
import { Page } from '@model/common';
import { Guest } from '@model/iceberg/booking/basket/guest/guest';
import { navigate } from '@state/basket/basketActions';
import { HYDRATE } from 'next-redux-wrapper';
import { isEqual } from 'lodash';
import { DealFinderResultsActions } from '@state/slices/deal-finder-results/dealFinderResultsSlice';
import { fetchTours } from '@state/slices/search-results/tours/toursSlice';
import { PackageReferencesActions } from '@state/slices/app';
import { setTourReferences } from '@state/slices/tours/tour-references';
import { ModalType, showModal } from '@state/slices/modal/modalSlice';
import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { getError } from '@util/error';

export enum GuestsActions {
  NAME = '@GUESTS',
  PERFORM_FETCH_GUESTS = '@GUESTS/PERFORM_FETCH_GUESTS',
  UPDATE_GUEST_FORM = '@GUESTS/updateGuestForm',
  SET_GUESTS = '@GUESTS/SET_GUESTS',
  RECEIVE_GUESTS_SUCCESS = '@GUESTS/RECEIVE_GUESTS_SUCCESS',
  RECEIVE_GUESTS_FAILURE = '@GUESTS/RECEIVE_GUESTS_FAILURE',
  INVALID_SEAT_SELECTION = '@GUESTS/invalidSeatSelection'
}

export enum KNOWN_ERRORS {
  INVALID_SEAT_SELECTION = 'One or more child found on exit seats',
  SESSION_EXPIRED = 'session expired'
}

interface SetGuestsPayload {
  searchToken: string;
  guests: Array<GuestFormValues>;
}

export interface GuestsState {
  data: Array<GuestFormValues>;
  loading: boolean;
  error: string | null;
}

export const INITIAL_GUESTS_STATE: GuestsState = {
  data: [],
  loading: false,
  error: null
};

export const setGuests = createAsyncThunk(
  GuestsActions.SET_GUESTS,
  async (payload: SetGuestsPayload, { dispatch, rejectWithValue }) => {
    const guestPayload: Array<Guest> = GuestMapper.getGuestsPayload(payload.guests);

    try {
      const api: GuestsApi = new GuestsApi();
      const response: GuestsResponse = await api.put(payload.searchToken, guestPayload);

      dispatch(navigate(Page.PAYMENT));
      return GuestMapper.getGuestFormValues(response.guests);
    } catch (error: any) {
      const message = error.response?.data?.message;

      if (message) {
        switch (message) {
          case KNOWN_ERRORS.INVALID_SEAT_SELECTION:
            dispatch(invalidSeatSelection());
            break;
          case KNOWN_ERRORS.SESSION_EXPIRED:
            dispatch(showModal({ modalType: ModalType.BASKET_ERROR, modalData: error.response?.data }));
            break;
        }
      }
      return rejectWithValue(message || error.message || 'Failed to fetch guests');
    }
  }
);

export const fetchGuests = createAsyncThunk(
  GuestsActions.PERFORM_FETCH_GUESTS,
  async ({ searchToken }: { searchToken: string }, { rejectWithValue }) => {
    try {
      const api: GuestsApi = new GuestsApi();
      const response: GuestsResponse = await api.get(searchToken);
      return GuestMapper.getGuestFormValues(response.guests);
    } catch (e: any) {
      return rejectWithValue(getError(e));
    }
  }
);

const guestsSlice = createSlice({
  name: GuestsActions.NAME,
  initialState: INITIAL_GUESTS_STATE,
  reducers: {
    updateGuestForm: (state, action: PayloadAction<Array<GuestFormValues>>) => {
      state.data = action.payload;
      state.error = null;
    },
    invalidSeatSelection: (state) => {
      state.error = 'Invalid seat selection detected';
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(HYDRATE, (state, action: any) => {
        if (isEqual(state.data, INITIAL_GUESTS_STATE.data)) {
          return {
            ...state,
            data: action.payload.guests?.data || state.data
          };
        }
        return state;
      })
      .addCase(setGuests.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(setGuests.fulfilled, (state, action) => {
        state.data = action.payload;
        state.loading = false;
        state.error = null;
      })
      .addCase(setGuests.rejected, (state, action) => {
        state.loading = false;
        const payload = action.payload as any;
        if (payload?.type === GuestsActions.INVALID_SEAT_SELECTION) {
          state.error = payload as string;
        } else {
          state.error = payload as string;
        }
      })
      .addCase(fetchGuests.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchGuests.fulfilled, (state, action) => {
        state.data = action.payload;
        state.loading = false;
        state.error = null;
      })
      .addCase(fetchGuests.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(DealFinderResultsActions.CLEAR_RESULTS, () => INITIAL_GUESTS_STATE)
      .addCase(PackageReferencesActions.SET_PACKAGE_REFERENCES, () => INITIAL_GUESTS_STATE)
      .addCase(fetchTours.fulfilled, () => INITIAL_GUESTS_STATE)
      .addCase(setTourReferences.fulfilled, () => INITIAL_GUESTS_STATE);
  }
});

export const { updateGuestForm, invalidSeatSelection } = guestsSlice.actions;
export const guestsReducer = guestsSlice.reducer;
